/**
 * Componente SeleccionCuenta
 * Muestra sugerencia automática del motor de contabilización
 * Permite al usuario aceptar o modificar las cuentas
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, Chip, Divider,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api';

const SeleccionCuenta = ({
  compraData,
  cuentas,
  configuracion,
  onActualizar,
}) => {
  const [sugerencia, setSugerencia] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [modoManual, setModoManual] = useState(false);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const empresaId = user.empresaId || '';

  const cuentasMovimiento = Array.isArray(cuentas) ? cuentas.filter(c => c.acepta_movimiento) : [];

  // Obtener sugerencia automática al montar
  useEffect(() => {
    obtenerSugerencia();
  }, []);

  const obtenerSugerencia = async () => {
    setCargando(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/compras-dinamico/sugerir-asiento`,
        {
          items: compraData.items,
          forma_pago: compraData.forma_pago || 'credito',
          aplicar_retenciones: compraData.aplicar_retenciones,
          retenciones: compraData.retenciones,
          proveedor_nombre: compraData.proveedor_nombre,
          iva_total: compraData.items.reduce((sum, i) => sum + (parseFloat(i.subtotal || 0) * 0.15), 0),
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'empresa-id': empresaId,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setSugerencia(response.data.data);
        // Auto-asignar cuentas sugeridas
        const asiento = response.data.data.asiento || [];
        const cuentaProveedores = asiento.find(a => a.tipo === 'PAGO');
        const cuentaIVA = asiento.find(a => a.tipo === 'IVA');
        if (cuentaProveedores?.cuenta_id) onActualizar('cuenta_proveedores_id', cuentaProveedores.cuenta_id);
        if (cuentaIVA?.cuenta_id) onActualizar('cuenta_iva_compras_id', cuentaIVA.cuenta_id);
      }
    } catch (err) {
      setError('No se pudo generar sugerencia automática. Configure manualmente.');
      setModoManual(true);
    } finally {
      setCargando(false);
    }
  };

  const totalDebe = sugerencia?.asiento?.filter(a => a.debe > 0).reduce((s, a) => s + a.debe, 0) || 0;
  const totalHaber = sugerencia?.asiento?.filter(a => a.haber > 0).reduce((s, a) => s + a.haber, 0) || 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Paso 4: Asiento Contable</Typography>

      {cargando && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={24} />
          <Typography>Generando asiento automático...</Typography>
        </Box>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {sugerencia && !modoManual && (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }} icon={<AutoFixHighIcon />}>
            Asiento generado automáticamente con <strong>{sugerencia.confianza}%</strong> de confianza.
            {sugerencia.confianza < 70 && ' Se recomienda revisar.'}
          </Alert>

          {/* Clasificaciones */}
          <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>Clasificación de Items:</Typography>
            {sugerencia.clasificaciones?.map((c, i) => (
              <Chip
                key={i}
                label={`${c.item?.substring(0, 20)} → ${c.tipo} (${c.confianza}%)`}
                size="small"
                color={c.confianza >= 80 ? 'success' : c.confianza >= 50 ? 'warning' : 'default'}
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            ))}
          </Paper>

          {/* Asiento sugerido */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a237e', '& th': { color: '#fff', fontWeight: 'bold' } }}>
                  <TableCell>Cuenta</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">DEBE</TableCell>
                  <TableCell align="right">HABER</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sugerencia.asiento?.map((linea, i) => (
                  <TableRow key={i} sx={{ bgcolor: linea.debe > 0 ? '#e8f5e9' : '#ffebee' }}>
                    <TableCell><strong>{linea.cuenta_codigo}</strong></TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{linea.cuenta_nombre}</TableCell>
                    <TableCell>
                      <Chip label={linea.tipo} size="small" sx={{ fontSize: '0.65rem' }}
                        color={linea.tipo === 'INVENTARIO' ? 'primary' : linea.tipo === 'GASTO' ? 'warning' : linea.tipo === 'IVA' ? 'info' : 'default'} />
                    </TableCell>
                    <TableCell align="right">{linea.debe > 0 ? `$${linea.debe.toFixed(2)}` : ''}</TableCell>
                    <TableCell align="right">{linea.haber > 0 ? `$${linea.haber.toFixed(2)}` : ''}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: '#666' }}>{linea.motivo}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell colSpan={3}><strong>TOTALES</strong></TableCell>
                  <TableCell align="right"><strong>${totalDebe.toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>${totalHaber.toFixed(2)}</strong></TableCell>
                  <TableCell>
                    {Math.abs(totalDebe - totalHaber) < 0.02
                      ? <Chip label="✓ Cuadrado" size="small" color="success" />
                      : <Chip label="✗ Descuadrado" size="small" color="error" />
                    }
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setModoManual(true)}>
              Modificar Manualmente
            </Button>
            <Button variant="outlined" onClick={obtenerSugerencia}>
              Regenerar Sugerencia
            </Button>
          </Box>
        </Box>
      )}

      {/* Modo Manual - Editar asiento directamente */}
      {modoManual && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Modifique las cuentas contables del asiento. Seleccione la cuenta correcta para cada línea.
          </Alert>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a237e', '& th': { color: '#fff', fontWeight: 'bold' } }}>
                  <TableCell>Cuenta</TableCell>
                  <TableCell align="right">DEBE</TableCell>
                  <TableCell align="right">HABER</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sugerencia?.asiento || []).map((linea, i) => (
                  <TableRow key={i} sx={{ bgcolor: linea.debe > 0 ? '#e8f5e9' : '#ffebee' }}>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={linea.cuenta_id || linea.cuenta_codigo || ''}
                          onChange={(e) => {
                            const nuevasSugerencias = { ...sugerencia };
                            const cuentaSel = cuentasMovimiento.find(c => c.id === e.target.value);
                            nuevasSugerencias.asiento[i] = {
                              ...nuevasSugerencias.asiento[i],
                              cuenta_id: e.target.value,
                              cuenta_codigo: cuentaSel?.codigo || '',
                              cuenta_nombre: cuentaSel?.nombre || '',
                            };
                            setSugerencia(nuevasSugerencias);
                            // Actualizar cuentas en compraData
                            if (linea.tipo === 'PAGO') onActualizar('cuenta_proveedores_id', e.target.value);
                            if (linea.tipo === 'IVA') onActualizar('cuenta_iva_compras_id', e.target.value);
                          }}
                          displayEmpty
                        >
                          {cuentasMovimiento.map(c => (
                            <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>
                              {c.codigo} - {c.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">{linea.debe > 0 ? `$${linea.debe.toFixed(2)}` : ''}</TableCell>
                    <TableCell align="right">{linea.haber > 0 ? `$${linea.haber.toFixed(2)}` : ''}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{linea.motivo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {sugerencia && (
            <Button variant="text" sx={{ mt: 1 }} onClick={() => setModoManual(false)}>
              ← Volver a sugerencia automática
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SeleccionCuenta;
